import { useMemo, useCallback } from "react";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";

import { useAddPhytosanitaryProgramRows, key as phytosanitaryProgramKey } from "@/hooks/planning/phytosanitary-program/usePhytosanitaryProgram";
import { usePhytosanitaryApplicationModalUrl } from "./usePhytosanitaryApplicationModalUrl";

export function usePhytosanitaryApplicationFormController({
	applicationForm,
	lookups,
	filtersForQuery,
	estateId,
	estateParentId,
	tableState,
	programData,
	setInfoMessages,
	setMessageType,
}) {
	const queryClient = useQueryClient();
	const { mutate: mutateAddRows, isLoading: isLoadingAddRows } = useAddPhytosanitaryProgramRows();

	const { applicationFormMode, applicationFormRow, applicationFormRows, openNewApplication, openAddProduct, closeApplicationForm } =
		usePhytosanitaryApplicationModalUrl({ applicationForm, programData });

	const isIrrigation = useMemo(() => {
		if (applicationFormMode === "add_product") {
			return applicationFormRow?.application_order_type_name?.toLowerCase()?.includes("riego") || false;
		}
		if (applicationFormMode === "new_application") {
			if (applicationForm.formState.isNewProgram) {
				const oaType = (lookups?.application_types || []).find((type) => type.value === applicationForm.formState.newProgramOATypeCustomerID);
				return oaType?.label?.toLowerCase()?.includes("riego") || false;
			}
			const selectedProgram = (lookups?.phytosanitary_programs || []).find(
				(program) => program.value === applicationForm.formState.phytosanitaryProgramID,
			);
			if (!selectedProgram) return false;
			const oaType = (lookups?.application_types || []).find((type) => type.value === selectedProgram.oa_type_customer_id);
			return oaType?.label?.toLowerCase()?.includes("riego") || false;
		}
		return false;
	}, [
		applicationFormMode,
		applicationFormRow,
		applicationForm.formState.phytosanitaryProgramID,
		applicationForm.formState.isNewProgram,
		applicationForm.formState.newProgramOATypeCustomerID,
		lookups,
	]);

	const handleNewApplication = useCallback(() => {
		openNewApplication();
	}, [openNewApplication]);

	const handleAddProduct = useCallback(
		(row) => {
			openAddProduct(row);
		},
		[openAddProduct],
	);

	const handleSubmitApplicationForm = useCallback(() => {
		if (!applicationForm.validate(applicationFormMode)) return;

		const isNewProgramMode = applicationFormMode === "new_application" && applicationForm.formState.isNewProgram;

		const rows = applicationForm.mapToRows();
		if (rows.length === 0) return;

		const queryFilters = {
			...filtersForQuery,
			program: filtersForQuery.program != null ? String(filtersForQuery.program) : "",
			specie: Number(filtersForQuery.specie),
			phenological_state: filtersForQuery.phenological_state != null ? String(filtersForQuery.phenological_state) : "",
			objective: filtersForQuery.objective != null ? String(filtersForQuery.objective) : "",
			variety: filtersForQuery.variety != null ? String(filtersForQuery.variety) : "",
			type: filtersForQuery.type != null ? String(filtersForQuery.type) : "",
			estate_id: estateId,
			estate_parent_id: estateParentId,
			...tableState,
		};

		const payload = {
			phytosanitary_program_id: isNewProgramMode
				? 0
				: applicationFormMode === "new_application"
					? Number(applicationForm.formState.phytosanitaryProgramID)
					: Number(applicationFormRow?.id),
			application_number: Number(applicationForm.formState.applicationNumber),
			period_text: applicationForm.formState.periodText || "",
			phenological_state_id: applicationForm.formState.phenologicalStateID ? Number(applicationForm.formState.phenologicalStateID) : undefined,
			phenological_state_text: applicationForm.formState.phenologicalStateText || "",
			wetness: applicationForm.formState.wetness === null ? undefined : Number(applicationForm.formState.wetness),
			rows,
			filters: queryFilters,
		};

		if (isNewProgramMode) {
			payload.new_program = {
				name: applicationForm.formState.newProgramName.trim(),
				estate_parent_id: applicationForm.formState.newProgramEstateParentID
					? Number(applicationForm.formState.newProgramEstateParentID)
					: Number(estateParentId) || 0,
				crop_customer_id: Number(applicationForm.formState.newProgramCropCustomerID),
				crop_detail_id: applicationForm.formState.newProgramCropDetailID ? Number(applicationForm.formState.newProgramCropDetailID) : undefined,
				oa_type_customer_id: applicationForm.formState.newProgramOATypeCustomerID
					? Number(applicationForm.formState.newProgramOATypeCustomerID)
					: undefined,
			};
		}

		const listQueryKey = [phytosanitaryProgramKey, "list", { ...filtersForQuery, estate_id: estateId, estate_parent_id: estateParentId, ...tableState }];

		mutateAddRows(payload, {
			onSuccess: (response) => {
				toast.success(response?.message || (isNewProgramMode ? "Programa y aplicación creados correctamente" : "Filas agregadas correctamente"));
				queryClient.setQueryData(listQueryKey, response);
				if (isNewProgramMode) {
					queryClient.invalidateQueries([phytosanitaryProgramKey, "lookups"]);
					queryClient.invalidateQueries([phytosanitaryProgramKey, "progress"]);
				}
				closeApplicationForm();
			},
			onError: (error) => {
				if (error?.response?.data?.messages && error.response.data.messages.length > 0) {
					setInfoMessages(error.response.data.messages);
					setMessageType(error.response.data.message_type || "error");
				} else {
					toast.error(error?.response?.data?.message || "Error al guardar");
				}
			},
		});
	}, [
		applicationForm,
		applicationFormMode,
		applicationFormRow,
		filtersForQuery,
		estateId,
		estateParentId,
		tableState,
		queryClient,
		mutateAddRows,
		closeApplicationForm,
		setInfoMessages,
		setMessageType,
	]);

	return {
		applicationFormMode,
		applicationFormRow,
		applicationFormRows,
		isIrrigation,
		isLoadingAddRows,
		handleNewApplication,
		handleAddProduct,
		closeApplicationForm,
		handleSubmitApplicationForm,
	};
}
